'use client';

import { FC, useMemo } from 'react';

import { DateRange } from 'react-day-picker';

import { BreakdownsResult, EMetric, metricDictionary } from 'api/useAnalytics';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { ChartContainer } from 'components/ui/chart';

import { AnalyticsBox } from '../AnalyticsBox';
import { chartConfig } from '../analyticsChart.config';
import { BarsTooltip } from '../AnalyticsChartTooltip/BarsTooltip';
import { AnalyticsLoadingBox } from '../AnalyticsLoadingBox';
import {
  BreakdownSortConfig,
  BreakdownSortField,
  convertToNumber,
  convertToPercentage,
  formatValue,
  sortBreakdownData
} from '../utils';

export type { BreakdownSortConfig };
export { BreakdownSortField };

type AnalyticsBarsBoxProps = {
  isLoading: boolean;
  metricName: EMetric;
  currentBreakdown: BreakdownsResult;
  comparedBreakdown?: BreakdownsResult;
  dateRange: DateRange;
  comparedDateRange?: DateRange;
  sortConfig?: BreakdownSortConfig;
};

const CATEGORY_GAP = 35;
const CATEGORY_GAP_WITH_COMPARISON = 60;

export const AnalyticsBarsBox: FC<AnalyticsBarsBoxProps> = ({
  isLoading,
  metricName,
  currentBreakdown,
  comparedBreakdown,
  dateRange,
  comparedDateRange,
  sortConfig
}) => {
  const isPercentageType = currentBreakdown.type === EStatisticType.PERCENTAGE;

  const processedData = useMemo(() => {
    return currentBreakdown.values.map((item) => {
      const prevItem = comparedBreakdown?.values.find(
        (prev) => prev.breakdownValue === item.breakdownValue
      );

      let value = convertToNumber(item.value);
      let previousValue = convertToNumber(prevItem?.value);

      if (isPercentageType) {
        value = convertToPercentage(value);
        previousValue = convertToPercentage(previousValue);
      }

      return {
        breakdownValue: String(item.breakdownValue),
        value,
        previousValue
      };
    });
  }, [currentBreakdown.values, comparedBreakdown?.values, isPercentageType]);

  const chartData = useMemo(() => {
    return sortConfig
      ? sortBreakdownData(processedData, sortConfig)
      : processedData;
  }, [processedData, sortConfig]);

  const gap = useMemo(
    () => (comparedBreakdown ? CATEGORY_GAP_WITH_COMPARISON : CATEGORY_GAP),
    [comparedBreakdown]
  );

  const height = useMemo(() => {
    const baseHeight = chartData.length * gap;
    const minHeight = comparedBreakdown ? 175 : 100;
    return Math.max(baseHeight, minHeight);
  }, [chartData.length, gap, comparedBreakdown]);

  if (isLoading) return <AnalyticsLoadingBox />;
  return (
    <AnalyticsBox metricName={metricName} breakdown={currentBreakdown}>
      <div className="max-h-[350px] overflow-y-auto overflow-x-hidden w-full">
        <div style={{ height: `${height}px` }}>
          <ChartContainer config={chartConfig} className="w-full h-full">
            <BarChart
              layout="vertical"
              margin={{ right: 50, left: 5, bottom: 5, top: 5 }}
              data={chartData}
              barSize={20}
              barGap={2}
              maxBarSize={20}
              height={height}
            >
              <defs>
                <pattern
                  id="pattern-previous"
                  patternUnits="userSpaceOnUse"
                  width="6"
                  height="6"
                  patternTransform="rotate(45)"
                >
                  <line
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="6"
                    stroke={chartConfig.previous.color}
                    strokeWidth="2"
                  />
                </pattern>
              </defs>
              <CartesianGrid
                vertical={false}
                horizontal={false}
                stroke="var(--base-border)"
              />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={false}
                hide
              />
              <YAxis
                dataKey="breakdownValue"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--base-muted-foreground)', fontSize: 12 }}
                width={50}
              />
              <Tooltip
                content={
                  <BarsTooltip
                    dateRange={dateRange}
                    comparedDateRange={comparedDateRange}
                    metricType={currentBreakdown.type}
                  />
                }
                cursor={false}
              />
              <Bar
                dataKey="value"
                fill={chartConfig.current.color}
                name={`${metricDictionary[metricName].title}`}
                radius={2}
              >
                <LabelList
                  dataKey="value"
                  position="right"
                  fill="var(--base-muted-foreground)"
                  fontSize={12}
                  formatter={(value: number) =>
                    formatValue(value, currentBreakdown.type)
                  }
                />
              </Bar>
              {comparedBreakdown && (
                <Bar
                  dataKey="previousValue"
                  fill="url(#pattern-previous)"
                  name={'Compared period'}
                  radius={2}
                >
                  <LabelList
                    dataKey="previousValue"
                    position="right"
                    fill="var(--base-muted-foreground)"
                    fontSize={12}
                    formatter={(value: number) =>
                      formatValue(value, currentBreakdown.type)
                    }
                  />
                </Bar>
              )}
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    </AnalyticsBox>
  );
};
